import { apiClient } from './apiClient';

export type SalesProductSummary = {
  id: string;
  name: string;
  slug: string;
  logoBase64: string;
  elevatorPitch?: string;
  isActive: boolean;
  sortOrder: number;
};

export type SalesProduct = SalesProductSummary & {
  coverImageBase64: string;
  elevatorPitchHtml: string;
  valuePropositionHtml: string;
  descriptionHtml: string;
  executiveSummaryHtml: string;
  modulesAndFeaturesHtml: string;
  featuresHtml: string;
  icpHtml: string;
  segmentsHtml: string;
  sortOrder: number;
  createdByUserId: string;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SalesProductListResult = {
  items: SalesProductSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type SalesProductListFilter = {
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
};

export type SalesProductUpsertPayload = {
  name: string;
  logoBase64?: string;
  coverImageBase64?: string;
  elevatorPitchHtml?: string;
  valuePropositionHtml?: string;
  descriptionHtml?: string;
  executiveSummaryHtml?: string;
  modulesAndFeaturesHtml?: string;
  featuresHtml?: string;
  icpHtml?: string;
  segmentsHtml?: string;
  isActive: boolean;
  sortOrder: number;
};

export async function listSalesProducts(filter: SalesProductListFilter = {}): Promise<SalesProductListResult> {
  const response = await apiClient.get<SalesProductListResult>('/v1/sales/products', {
    params: filter,
  });
  return {
    items: response.data.items ?? [],
    totalCount: response.data.totalCount ?? 0,
    page: response.data.page ?? 1,
    pageSize: response.data.pageSize ?? 25,
  };
}

export async function getSalesProduct(id: string): Promise<SalesProduct> {
  const response = await apiClient.get<SalesProduct>(`/v1/sales/products/${id}`);
  return response.data;
}

export async function createSalesProduct(payload: SalesProductUpsertPayload): Promise<SalesProduct> {
  const response = await apiClient.post<SalesProduct>('/v1/sales/products', payload);
  return response.data;
}

export async function updateSalesProduct(id: string, payload: SalesProductUpsertPayload): Promise<SalesProduct> {
  const response = await apiClient.put<SalesProduct>(`/v1/sales/products/${id}`, payload);
  return response.data;
}

export async function deleteSalesProduct(id: string): Promise<void> {
  await apiClient.delete(`/v1/sales/products/${id}`);
}

export type SalesProductImportQueued = {
  importId: string;
  hangfireJobId?: string | null;
  status: string;
  message: string;
};

export type SalesProductImportStatus = {
  id: string;
  originalFileName: string;
  status: string;
  hangfireJobId?: string | null;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  errorSummary?: string | null;
  createdAtUtc: string;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
};

export async function downloadSalesProductImportTemplate(): Promise<Blob> {
  const response = await apiClient.get('/v1/sales/products/import/template', {
    responseType: 'blob',
  });
  return response.data;
}

export async function importSalesProducts(file: File): Promise<SalesProductImportQueued> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<SalesProductImportQueued>(
    '/v1/sales/products/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function getSalesProductImportStatus(
  importId: string,
): Promise<SalesProductImportStatus> {
  const { data } = await apiClient.get<SalesProductImportStatus>(
    `/v1/sales/products/import/${importId}`,
  );
  return data;
}
